import { graphql } from '@apollo/client/react/hoc';
import React, { useState } from 'react';
import { compose } from 'recompose';
import LinkButton from '@/se/components/LinkButton';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';

import { discharge, item } from '../../../../graph/patients';
import { useScope } from '@/hooks/useScope';
import { useRetainedBeaconDischarge } from '@/hooks/useRetainedBeaconDischarge';
import {
  RETAINED_BEACON_DISCHARGE_COPY,
  RetainedBeaconDischargeActions,
  RetainedBeaconDischargeChoice,
} from './RetainedBeaconDischargeChoice';

const BaseDischargeLinkButton = ({ id, mutate }) => {
  const scope = useScope();
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const retainedBeacon = useRetainedBeaconDischarge({
    patientId: id,
    enabled: scope?.hospital?.modules?.retainedBeaconInpatient?.enabled === true,
    surface: 'patient-details',
  });

  const close = () => {
    retainedBeacon.reset();
    setErrorMessage(null);
    setOpen(false);
  };

  const begin = () => {
    setErrorMessage(null);
    retainedBeacon.begin();
    setOpen(true);
  };

  const handleDischarge = async choice => {
    if (choice == null && !retainedBeacon.canSubmit) return;
    setWorking(true);
    try {
      await mutate({ variables: { id, keepBeacon: retainedBeacon.prepareSubmission(choice) } });
      close();
    } catch (error) {
      console.error('Discharge of a patient failed', error.message);
      if (error?.graphQLErrors?.some(graphQLError => graphQLError.message === 'RetainedDischargeConflict')) {
        setErrorMessage(RETAINED_BEACON_DISCHARGE_COPY.conflictError);
      } else {
        setErrorMessage('Discharge failed. Please try again.');
      }
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <LinkButton onClick={begin}>Discharge</LinkButton>
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby="patient-discharge-title">
        <DialogTitle id="patient-discharge-title">{RETAINED_BEACON_DISCHARGE_COPY.title}</DialogTitle>
        <DialogContent>
          {retainedBeacon.requiresChoice ? (
            <RetainedBeaconDischargeChoice eligibility={retainedBeacon.eligibility} />
          ) : (
            <DialogContentText>Are you sure you want to discharge this patient?</DialogContentText>
          )}
          {errorMessage && <DialogContentText color="error">{errorMessage}</DialogContentText>}
        </DialogContent>
        <DialogActions style={{ flexWrap: 'wrap', gap: 8, padding: '16px 24px' }}>
          {retainedBeacon.requiresChoice ? (
            <RetainedBeaconDischargeActions onChoose={handleDischarge} onCancel={close} working={working} />
          ) : (
            <>
              <Button onClick={close} color="secondary" disabled={working}>
                {RETAINED_BEACON_DISCHARGE_COPY.cancel}
              </Button>
              <Button onClick={() => handleDischarge(null)} color="primary" variant="contained" disabled={working}>
                Discharge patient
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default compose(
  graphql(discharge, {
    options: ({ id }) => ({
      refetchQueries: [
        {
          query: item,
          variables: {
            id: parseInt(id, 10),
          },
        },
      ],
    }),
  })
)(BaseDischargeLinkButton);
